import { Checkbox, CircularProgress, Typography } from '@mui/material'
import React, { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import tw from 'twin.macro'
import { createAsyncAction } from 'src/utils/reduxUtils'
import {
  FetchNhtsaDecodedVin,
  UploadVehicleInsurance,
  SetNewDocsUploaded,
} from 'src/constants/actionTypes'
import { getCurrentCollectDataStep } from 'src/selectors/collectData'
import { Formik, FormikProps } from 'formik'
import { getVehicleInsuranceSchema, validateVinSchema } from './VehicleInsuranceForm.validator'
import { FormikFileInput } from '../shared/form/formik/FormikFileInput'
import { VehicleInsuranceFormValues } from './VehicleInsuranceForm.interface'
import { FormErrorMessage } from '../shared/form/FormErrorMessage'
import { FormikTextField } from '../shared/form/formik/FormikTextField'
import { FormikSelect } from '../shared/form/formik/FormikSelect'
import { nonInsuredVehicles, vehicleFormTypes, VehicleType } from 'src/constants/vehicle'
import { ShipmentMajor } from '@shopify/polaris-icons'
import { InfoContainer } from '../shared/InfoContainer'
import { CommonModal } from '../shared/CommonModal'
import { BulletPointText } from '../shared/BulletPointText'
import { BasicRadioGroup } from '../shared/form/BasicRadioGroup'
import { NhtsaDecodedVin } from 'src/interfaces/nhtsa'
import RecollectIcon from 'src/assets/icons/recollect.svg'
import vehicleVinExample from 'src/assets/images/vehicle-vin-example.png'
import vehicleInsuranceExample from 'src/assets/images/vehicle-insurance-example.png'
import { Button } from '../shared/Button'
import { Driver } from 'src/interfaces/driver'
import { VehicleInsuranceCollectDataStep } from 'src/interfaces/collectData'
import { DataCollectionSource } from 'src/constants/shared'
import { OfflineBanner } from '../shared/OfflineBanner'
import { useOnlineStatus } from 'src/utils/hooks/useOnlineStatus'
import { useStepFormStorage } from 'src/hooks/useStepFormStorage'
import { SecureWrapper } from '../layout/SecureWrapper'
import { StepLabels } from '../client/step-labels/StepLabels'

interface Props {
  driver?: Driver
}

const USER_VEHICLE_TYPE = {
  sameVehicle: 'sameVehicle',
  newVehicle: 'newVehicle',
}

const defaultValues: VehicleInsuranceFormValues = {
  file: undefined,
  policyDocument: undefined,
  type: VehicleType.Sedan,
  carMake: '',
  carModel: '',
  vin: '',
  year: '',
}

const YEAR_MASK = [/\d/, /\d/, /\d/, /\d/]
const VIN_MASK = [
  /[a-zA-Z0-9]/,
  /[a-zA-Z0-9]/,
  /[a-zA-Z0-9]/,
  /[a-zA-Z0-9]/,
  /[a-zA-Z0-9]/,
  /[a-zA-Z0-9]/,
  /[a-zA-Z0-9]/,
  /[a-zA-Z0-9]/,
  /[a-zA-Z0-9]/,
  /[a-zA-Z0-9]/,
  /[a-zA-Z0-9]/,
  /[a-zA-Z0-9]/,
  /[a-zA-Z0-9]/,
  /[a-zA-Z0-9]/,
  /[a-zA-Z0-9]/,
  /[a-zA-Z0-9]/,
  /[a-zA-Z0-9]/,
]

const VehicleInsuranceFormComponent = ({ driver }: Props) => {
  const dispatch = useDispatch()

  const currentStep = useSelector(getCurrentCollectDataStep) as VehicleInsuranceCollectDataStep

  const formikRef = useRef<FormikProps<VehicleInsuranceFormValues>>(null)

  const [showPolicyFileInput, setShowPolicyFileInput] = useState<boolean>(false)
  const [showVinModal, setShowVinModal] = useState<boolean>(false)
  const [showInsuranceModal, setShowInsuranceModal] = useState<boolean>(false)
  const [vehicleType, setVehicleType] = useState<VehicleType>(
    driver?.vehicle?.type || VehicleType.Sedan,
  )
  const [sameVehicleType, setSameVehicleType] = useState<string>(
    driver?.collectDataFlow === DataCollectionSource.Recollection && driver?.vehicle?.type
      ? USER_VEHICLE_TYPE.sameVehicle
      : USER_VEHICLE_TYPE.newVehicle,
  )
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const isOnline = useOnlineStatus()

  const [decodedVin, setDecodedVin] = useState<NhtsaDecodedVin | null>(null)
  const [isVinDecodeLoading, setIsVinDecodeLoading] = useState<boolean>(false)
  const [isVinDecodeError, setIsVinDecodeError] = useState<boolean>(false)

  const { savedValues, onValuesChange, clearStorage } =
    useStepFormStorage<VehicleInsuranceFormValues>({
      storageKey: 'step_vehicle_insurance_form',
      fileFields: ['file', 'policyDocument'],
    })

  const initialValues = savedValues ?? defaultValues

  const isVinDecoded = decodedVin || isVinDecodeError
  const isInsuranceUploadDisabled = currentStep?.metadata?.disableInsuranceUpload || false

  const isSameVehicle = useMemo(
    () => sameVehicleType === USER_VEHICLE_TYPE.sameVehicle,
    [sameVehicleType],
  )

  useEffect(() => {
    if (isSameVehicle) {
      formikRef.current?.setFieldValue('type', driver?.vehicle?.type || '')
      setVehicleType(driver?.vehicle?.type || VehicleType.Sedan)
      formikRef.current?.setFieldValue('carMake', driver?.vehicle?.carMake || '')
      formikRef.current?.setFieldValue('carModel', driver?.vehicle?.carModel || '')
      formikRef.current?.setFieldValue('vin', driver?.vehicle?.vin || '')
      formikRef.current?.setFieldValue('year', driver?.vehicle?.year || '')
    } else {
      formikRef.current?.resetForm()
      setVehicleType(VehicleType.Sedan)
    }
  }, [isSameVehicle])

  const decodeVin = useCallback(async () => {
    setIsVinDecodeLoading(true)

    try {
      const decodedVinResponse: NhtsaDecodedVin = await createAsyncAction(
        dispatch,
        FetchNhtsaDecodedVin.request({
          stepId: currentStep?.id,
          vin: formikRef.current?.values.vin ?? '',
        }),
      )

      formikRef.current?.setFieldValue('carMake', decodedVinResponse.Make || '')
      formikRef.current?.setFieldValue('carModel', decodedVinResponse.Model || '')
      formikRef.current?.setFieldValue('year', decodedVinResponse.ModelYear || '')

      setDecodedVin(decodedVinResponse)
    } catch (err) {
      setIsVinDecodeError(true)
    }

    setIsVinDecodeLoading(false)
  }, [dispatch, currentStep?.id])

  const handleFormSubmit = async (values: VehicleInsuranceFormValues) => {
    if (!currentStep?.id) {
      return
    }

    if (!isVinDecoded && !nonInsuredVehicles.includes(vehicleType)) {
      decodeVin()

      return
    }

    setIsLoading(true)
    setError(null)

    const requestBody = {
      ...values,
      stepId: currentStep?.id,
    }

    if (!showPolicyFileInput) {
      delete requestBody.policyDocument
    }

    if (nonInsuredVehicles.includes(vehicleType)) {
      delete requestBody.carMake
      delete requestBody.carModel
      delete requestBody.vin
      delete requestBody.year
    }

    try {
      await createAsyncAction(dispatch, UploadVehicleInsurance.request(requestBody))
      clearStorage()

      if (driver?.collectDataFlow === DataCollectionSource.Recollection) {
        await createAsyncAction(dispatch, SetNewDocsUploaded.success(true))
      }
    } catch (err: any) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const openVinModal = useCallback(() => {
    setShowVinModal(true)
  }, [])

  const closeVinModal = useCallback(() => {
    setShowVinModal(false)
  }, [])

  const openInsuranceModal = useCallback(() => {
    setShowInsuranceModal(true)
  }, [])

  const closeInsuranceModal = useCallback(() => {
    setShowInsuranceModal(false)
  }, [])

  const handleShowPolicyFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowPolicyFileInput(event.target.checked)
  }

  const handleReset = useCallback(() => {
    setDecodedVin(null)
    setIsVinDecodeError(false)

    formikRef.current?.setFieldValue('carMake', '')
    formikRef.current?.setFieldValue('carModel', '')
    formikRef.current?.setFieldValue('vin', '')
    formikRef.current?.setFieldValue('year', '')
  }, [])

  const handleChangeUserVehicleType = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.value === USER_VEHICLE_TYPE.sameVehicle && formikRef.current) {
        formikRef.current.resetForm({ values: initialValues })
        setVehicleType(driver?.vehicle?.type || VehicleType.Sedan)
      } else {
        setVehicleType(VehicleType.Sedan)
      }

      handleReset()
      setSameVehicleType(event.target.value)
    },
    [driver?.vehicle?.type],
  )

  return (
    <Formik
      initialValues={initialValues}
      innerRef={formikRef}
      onSubmit={handleFormSubmit}
      validationSchema={
        !nonInsuredVehicles.includes(vehicleType) && !isVinDecoded
          ? validateVinSchema
          : getVehicleInsuranceSchema({
              withPolicyFile: showPolicyFileInput,
              isVehicleSchema: !nonInsuredVehicles.includes(vehicleType),
              isInsuranceUploadDisabled,
            })
      }
    >
      {({ handleSubmit, handleChange, values: formValues }) => {
        onValuesChange(formValues)

        return (
          <SecureWrapper
            footer={
              <Fragment>
                <OfflineBanner isOnline={isOnline} />
                <FormErrorMessage css={tw`mb-4`} error={error} />
                {!nonInsuredVehicles.includes(vehicleType) && !isVinDecoded ? null : (
                  <Button
                    className={tw`w-full mt-4`}
                    loading={isLoading}
                    disabled={!isOnline}
                    onClick={() => handleSubmit()}
                    data-testid="contractors-onboarding-vehicle-insurance-submit-btn"
                  >
                    Submit and continue
                  </Button>
                )}
              </Fragment>
            }
          >
            <div>
              <div
                css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mt-2 mb-4`}
              >
                <ShipmentMajor width={20} />
              </div>
              <StepLabels />
              <Typography css={tw`my-2`} variant="h2">
                {isInsuranceUploadDisabled ? 'Vehicle information' : 'Vehicle and insurance'}
              </Typography>
              <Typography css={tw`mb-4 text-shades-400`}>
                {`Please enter your vehicle details${
                  isInsuranceUploadDisabled
                    ? ''
                    : ' and upload your insurance card, along with any supporting documents'
                }.`}
              </Typography>
              <div css={tw`mb-6`}>
                <InfoContainer>
                  GigSafe onboarding currently supports one vehicle at a time. If insurance applies,
                  it will cover this vehicle only.
                </InfoContainer>
              </div>
              {driver?.collectDataFlow === DataCollectionSource.Recollection &&
                !!driver?.vehicle?.type && (
                  <div css={tw`mb-4`}>
                    <Typography css={tw`uppercase text-shades-400`} variant="overline">
                      Select your state
                    </Typography>
                    <BasicRadioGroup
                      value={sameVehicleType}
                      onChange={handleChangeUserVehicleType}
                      options={[
                        {
                          value: USER_VEHICLE_TYPE.sameVehicle,
                          label: "I'm using the same vehicle as before",
                        },
                        {
                          value: USER_VEHICLE_TYPE.newVehicle,
                          label: "I'm using new vehicle",
                        },
                      ]}
                      dataTestId="contractors-onboarding-vehicle-insurance-same-vehicle-input"
                    />
                  </div>
                )}
              <div css={tw`mb-4`}>
                <Typography css={tw`uppercase text-shades-400`} variant="overline">
                  Basic Information
                </Typography>
                <div css={tw`bg-shades-0 p-4 rounded-xl mt-2 relative overflow-hidden`}>
                  <div css={tw`w-full mb-4`}>
                    <FormikSelect
                      name="type"
                      label="Vehicle type"
                      placeholder="Select vehicle type"
                      options={vehicleFormTypes}
                      inputProps={{
                        'data-testid': 'contractors-onboarding-vehicle-insurance-type-input',
                      }}
                      onChange={(e) => {
                        const value = e?.target?.value as VehicleType

                        setVehicleType(value)
                        handleChange(e)

                        if (nonInsuredVehicles.includes(value)) {
                          setShowPolicyFileInput(false)
                        }
                      }}
                    />
                  </div>
                  {!nonInsuredVehicles.includes(vehicleType) && (
                    <Fragment>
                      <FormikTextField
                        name="vin"
                        css={tw`w-full mb-4`}
                        label="VIN number"
                        placeholder="Enter VIN (17 characters)"
                        disabled={isVinDecodeError || !!decodedVin}
                        mask={VIN_MASK}
                        inputProps={{
                          'data-testid': 'contractors-onboarding-vehicle-insurance-vin-input',
                        }}
                      />
                      {!isVinDecoded && (
                        <div>
                          <InfoContainer
                            onClick={openVinModal}
                            dataTestId="contractors-onboarding-vehicle-insurance-vin-learn-btn"
                          >
                            Learn more about VIN number
                          </InfoContainer>
                          <Button
                            className={tw`w-full mt-4`}
                            loading={isVinDecodeLoading}
                            onClick={() => handleSubmit()}
                            data-testid="contractors-onboarding-vehicle-insurance-vin-check-btn"
                          >
                            Check VIN number
                          </Button>
                        </div>
                      )}
                      {!!decodedVin && (
                        <InfoContainer color="success">
                          We found your vehicle info below. Please check for accuracy — if anything
                          looks incorrect, you can edit the details or start over by clicking 'Reset
                          and Try Again'.
                        </InfoContainer>
                      )}
                      {isVinDecodeError && (
                        <InfoContainer color="orange">
                          We couldn't find your vehicle info. Please enter the details manually
                          below, or click 'Reset and Try Again' to start over with a new VIN.
                        </InfoContainer>
                      )}
                      {isVinDecodeError || !!decodedVin ? (
                        <Button
                          className={tw`w-full mt-4`}
                          onClick={handleReset}
                          color="grey"
                          startIcon={<img src={RecollectIcon} />}
                          data-testid="contractors-onboarding-vehicle-insurance-vin-reset-btn"
                        >
                          Reset and Try Again
                        </Button>
                      ) : null}
                    </Fragment>
                  )}
                  {isVinDecodeLoading && (
                    <div
                      css={tw`absolute top-0 left-0 w-full h-full bg-[rgba(255, 255, 255, 0.95)] flex flex-col items-center justify-center`}
                    >
                      <CircularProgress />
                      <Typography css={tw`text-center mt-4 text-primary-500`}>
                        Looking for your VIN info... Please wait while we process your vehicle
                        information.
                      </Typography>
                    </div>
                  )}
                </div>
              </div>
              {!nonInsuredVehicles.includes(vehicleType) && isVinDecoded && (
                <div css={tw`mb-4`}>
                  <Typography css={tw`uppercase text-shades-400`} variant="overline">
                    Vehicle details
                  </Typography>
                  <div css={tw`bg-shades-0 p-4 rounded-xl mt-2`}>
                    <FormikTextField
                      name="year"
                      css={tw`w-full mb-4`}
                      label="Vehicle year*"
                      placeholder="Enter year of production"
                      mask={YEAR_MASK}
                      inputProps={{
                        'data-testid': 'contractors-onboarding-vehicle-insurance-year-input',
                      }}
                    />
                    <FormikTextField
                      name="carMake"
                      css={tw`w-full mb-4`}
                      label="Make*"
                      placeholder="Enter vehicle make E.g. Toyota"
                      inputProps={{
                        'data-testid': 'contractors-onboarding-vehicle-insurance-make-input',
                      }}
                    />
                    <FormikTextField
                      name="carModel"
                      css={tw`w-full mb-4`}
                      label="Model*"
                      placeholder="Enter vehicle model E.g. Tacoma XL"
                      inputProps={{
                        'data-testid': 'contractors-onboarding-vehicle-insurance-model-input',
                      }}
                    />
                    {decodedVin?.WMI && (
                      <div css={tw`w-full mb-4`}>
                        <Typography css={tw`mb-1 text-shades-400`} variant="h6">
                          WMI
                        </Typography>
                        <Typography>{decodedVin.WMI}</Typography>
                      </div>
                    )}
                    {decodedVin?.Axles && (
                      <div css={tw`w-full mb-4`}>
                        <Typography css={tw`mb-1 text-shades-400`} variant="h6">
                          Axles
                        </Typography>
                        <Typography>{decodedVin.Axles}</Typography>
                      </div>
                    )}
                    {decodedVin?.GVWR && (
                      <div css={tw`w-full mb-4`}>
                        <Typography css={tw`mb-1 text-shades-400`} variant="h6">
                          GVWR
                        </Typography>
                        <Typography>{decodedVin.GVWR}</Typography>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {isInsuranceUploadDisabled ||
              (!nonInsuredVehicles.includes(vehicleType) && !isVinDecoded) ? null : (
                <div css={tw`mb-4`}>
                  <Typography css={tw`uppercase text-shades-400`} variant="overline">
                    {nonInsuredVehicles.includes(vehicleType)
                      ? 'Upload Photo of the vehicle'
                      : 'Upload vehicle insurance'}
                  </Typography>
                  <div css={tw`bg-shades-0 p-4 rounded-xl mt-2`}>
                    {!nonInsuredVehicles.includes(vehicleType) && (
                      <Fragment>
                        <Typography css={tw`mb-4`} variant="h4">
                          Insurance requirements
                        </Typography>
                        <BulletPointText className={tw`mb-4`} index={1}>
                          The insurance must have you as an individual as a named insured OR
                          acknowledge you as insured on the schedule*
                        </BulletPointText>
                        <div css={tw`mb-4`}>
                          <InfoContainer>
                            *Please upload the complete policy document in the second box below to
                            prove you are on the schedule and there are no schedule exclusions.
                          </InfoContainer>
                        </div>
                        <BulletPointText className={tw`mb-4`} index={2}>
                          The insurance must be current and NOT expired.
                        </BulletPointText>
                        <BulletPointText className={tw`mb-4`} index={3}>
                          The insurance must be clearly visible and NOT blurred.
                        </BulletPointText>
                        <BulletPointText className={tw`mb-4`} index={4}>
                          Follow the example below to avoid uploading wrong document (do NOT upload
                          vehicle registration)
                        </BulletPointText>
                        <div css={tw`mb-4`}>
                          <InfoContainer
                            onClick={openInsuranceModal}
                            dataTestId="contractors-onboarding-vehicle-insurance-example-btn"
                          >
                            Vehicle insurance example
                          </InfoContainer>
                        </div>
                      </Fragment>
                    )}
                    <FormikFileInput
                      name="file"
                      inputDataTestId="contractors-onboarding-vehicle-insurance-card-file-input"
                    />
                  </div>
                </div>
              )}
              {!isInsuranceUploadDisabled &&
                !nonInsuredVehicles.includes(vehicleType) &&
                isVinDecoded && (
                  <div css={tw`mb-4`}>
                    <Typography css={tw`uppercase text-shades-400`} variant="overline">
                      {'FULL POLICY DOCUMENT UPLOAD (OPTIONAL)'}
                    </Typography>
                    <div css={tw`bg-shades-0 p-4 rounded-xl mt-2`}>
                      <div css={tw`flex flex-row items-center`}>
                        <Checkbox
                          checked={showPolicyFileInput}
                          onChange={handleShowPolicyFileInput}
                          inputProps={
                            {
                              'aria-label': 'controlled',
                              'data-testid':
                                'contractors-onboarding-vehicle-insurance-policy-checkbox-input',
                            } as React.InputHTMLAttributes<HTMLInputElement>
                          }
                        />
                        <div css={tw`ml-2`}>
                          <Typography variant="h4">
                            My name is not displayed on the insurance card
                          </Typography>
                          <Typography css={tw`mt-2 text-shades-500`}>
                            Upload addition document with your name on it (eg. Declaration
                            Page/Coverage Summary Page)
                          </Typography>
                        </div>
                      </div>
                      {showPolicyFileInput && (
                        <div css={tw`mt-4`}>
                          <FormikFileInput
                            name="policyDocument"
                            inputDataTestId="contractors-onboarding-vehicle-insurance-policy-file-input"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
            </div>
            <CommonModal
              title="About VIN number"
              text="A VIN is a unique , 17-character vehicle identification number. It can be found on your insurance card, title document, or registration. Highlighted in red on example."
              isOpen={showVinModal}
              image={vehicleVinExample}
              onClose={closeVinModal}
              closeButtonTestId="contractors-onboarding-vehicle-insurance-vin-modal-close-btn"
            />
            <CommonModal
              title="Insurance example"
              text="Follow the provided examples."
              isOpen={showInsuranceModal}
              image={vehicleInsuranceExample}
              onClose={closeInsuranceModal}
              closeButtonTestId="contractors-onboarding-vehicle-insurance-example-modal-close-btn"
            />
          </SecureWrapper>
        )
      }}
    </Formik>
  )
}

export const VehicleInsuranceForm = memo(VehicleInsuranceFormComponent)
